﻿import { faAdd, faCheckSquare, faSpinner, faSubtract } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { JSX, memo, useRef, useState, useCallback, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { NumberField } from "@base-ui/react/number-field";
import { Autocomplete, Checkbox, FormControlLabel } from "@mui/material";
import TextField from "@mui/material/TextField";
import { faSquare } from "@fortawesome/free-regular-svg-icons";

import { TableCellRenderHelpers, TableRow, TableValue } from "./DataTable";
import ButtonDatePicker from "../Buttons/ButtonFieldDatePicker";
import camelToCapitalized from "../../utilities/helpers/camelCaseToCapitalized";
import { reconcileDrafts } from "./draftReconciliation";

interface TableRowElementProps<IDType extends number | string = number> {
  tableRow: TableRow<IDType>;
  isLoading: boolean;
  showDetailsColumnHeadCell: boolean;
  rowIndex: number;
  numberOfRows: number;
  expandedRowRenderer?: (row: TableRow<IDType>) => React.ReactNode;
  rowTailwindClasses?: string;
  rowContainerTailwindClasses?: string;
  lockedTabKey?: string | null;
  registerTabInputRef?: (rowId: IDType, tabLockKey: string, el: HTMLElement | null) => void;
  focusNextLockedCell?: (rowId: IDType, tabLockKey: string) => void;
  /** Reports whether this row currently holds unsaved edit drafts, so the table
   *  can warn the user if the row is removed mid-edit by a live update. */
  onDraftStateChange?: (rowId: IDType, hasDrafts: boolean) => void;
  /** Disable the per-row fade-in. Used by the virtualized list, where rows
   *  mount/unmount on every scroll position and a fade would look like flicker. */
  disableMountAnimation?: boolean;
}

function TableRowElementComponent<IDType extends number | string = number>(
  {
    tableRow,
    isLoading,
    showDetailsColumnHeadCell,
    rowIndex,
    numberOfRows,
    expandedRowRenderer,
    rowTailwindClasses,
    rowContainerTailwindClasses,
    lockedTabKey,
    registerTabInputRef,
    focusNextLockedCell,
    onDraftStateChange,
    disableMountAnimation,
  }: TableRowElementProps<IDType>
) {
  const focusValueRef = useRef<Record<string, any>>({});
  const [isExpanded, setIsExpanded] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [draftValues, setDraftValues] = useState<Record<string, any>>({});

  const setDraft = useCallback((cellName: string, val: any) => {
    setDraftValues(prev => ({ ...prev, [cellName]: val }));
  }, []);

  const clearDraft = useCallback((cellName: string) => {
    setDraftValues(prev => {
      if (!(cellName in prev)) return prev;
      const { [cellName]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const getDisplayValue = (v: TableValue<IDType>) => v.name in draftValues ? draftValues[v.name] : v.value;
  const draftedRow: TableRow<IDType> = Object.keys(draftValues).length === 0
    ? tableRow
    : {
      ...tableRow,
      values: tableRow.values.map(cv =>
        cv.name in draftValues ? { ...cv, value: draftValues[cv.name] } : cv
      ),
    };

  useEffect(() => {
    setDraftValues(prev => reconcileDrafts(prev, tableRow.values) as Record<string, any>);
  }, [tableRow]);

  // Note: This is to report 'dirty' state up so the table can warn if this row is removed mid-edit.
  // Deliberately no unmount cleanup: a row deleted while dirty must stay "dirty"
  // long enough for the table to notice it vanished.
  useEffect(() => {
    onDraftStateChange?.(tableRow.id, Object.keys(draftValues).length > 0);
  }, [draftValues, tableRow.id, onDraftStateChange]);

  const rowBorderClasses = [
    "border-b", "border-theme-table-border",
    "transition-colors",
    rowTailwindClasses ?? ""
  ];

  const getCellFocusKey = (cellName: string) => `${String(tableRow.id)}::${cellName}`;

  const scheduleDebounced = (v: typeof tableRow.values[number], newValue: any) => {
    const timerKey = `${getCellFocusKey(v.name)}::debounce`;
    clearTimeout(focusValueRef.current[timerKey]);
    focusValueRef.current[timerKey] = setTimeout(async () => {
      delete focusValueRef.current[timerKey];
      setIsWorking(true);
      await v.onChangeDebounced!(draftedRow, newValue);
      clearDraft(v.name);
      setIsWorking(false);
    }, v.onChangeDebouncedMs ?? 600);
  };

  const toggleExpanded = async () => setIsExpanded(prev => !prev);

  const getCellHelpers = (v: typeof tableRow.values[number]): TableCellRenderHelpers => {
    const isTabLocked = !!lockedTabKey && !!v.tabLockKey && lockedTabKey === v.tabLockKey;
    const resolveCell = (cellName?: string) =>
      cellName ? tableRow.values.find(x => x.name === cellName) : v;
    return {
      tabLock: {
        isTabLocked,
        registerInputRef: (el) => {
          if (!v.tabLockKey || !registerTabInputRef) return;
          registerTabInputRef(tableRow.id, v.tabLockKey, el);
        },
        handleLockedTabbing: (e) => {
          if (!isTabLocked || !focusNextLockedCell) return;
          if (e.key !== "Tab" || e.shiftKey) return;
          e.preventDefault();
          setTimeout(() => focusNextLockedCell(tableRow.id, v.tabLockKey!), 0);
        },
      },
      row: {
        isSaving: isWorking,
        setIsSaving: setIsWorking,
      },
      draft: {
        get: (cellName) => {
          const target = resolveCell(cellName);
          if (!target) return undefined;
          return target.name in draftValues ? draftValues[target.name] : target.value;
        },
        set: (newValue, cellName) => {
          const target = resolveCell(cellName);
          if (!target) return;
          setDraft(target.name, newValue);
        },
        clear: (cellName) => {
          const target = resolveCell(cellName);
          if (!target) return;
          clearDraft(target.name);
        },
      },
    };
  };

  return isLoading ? (
    <div className="fade-up"><FontAwesomeIcon icon={faSpinner} spin /></div>
  ) : (
    <div
      className={`line ${disableMountAnimation ? '' : 'animate-fade-in'} ${rowContainerTailwindClasses ?? ''}`}
      id={`row-${tableRow.id}`}
    >
      <ul className={rowBorderClasses.filter(Boolean).join(" ")}>
        {draftedRow.values.map((v, i) => (
          <li
            key={i}
            className={`flex items-center text-theme-table-row-contrast justify-center text-[0.9rem] w-full transition ${v.tailwindClasses ?? ''} ${lockedTabKey && v.tabLockKey === lockedTabKey
              ? "bg-theme-warning border-x outline-2 outline-y outline-theme-warning border-theme-warning"
              : ""
              }`}
          >
            {v.render
              ? v.render(v, draftedRow, getCellHelpers(v))
              : v.editable
                ? renderEditableCell(v, i)
                : renderReadOnlyCell(v)
            }
          </li>
        ))}

        {showDetailsColumnHeadCell && (
          <li
            className="cursor-pointer text-center flex items-center justify-center text-[0.9rem] w-full basis-[5%]"
            onClick={toggleExpanded}
          >
            <i className="font-bold text-xl relative text-theme-table-row-contrast">
              <span className={`transition ease-in-out ${isExpanded ? 'opacity-0' : 'opacity-100'}`}>
                <FontAwesomeIcon icon={faAdd} />
              </span>
              <span className={`absolute inset-0 transition ease-in-out ${isExpanded ? 'opacity-100' : 'opacity-0'}`}>
                <FontAwesomeIcon icon={faSubtract} />
              </span>
            </i>
          </li>
        )}
      </ul>

      {expandedRowRenderer && (
        <AnimatePresence mode="sync">
          <motion.div
            key={`${isExpanded}`}
            className={isExpanded ? "border-y border-theme-table-border bg-theme-table-row-surface" : ""}
            initial={{ height: '0' }}
            animate={{ height: isExpanded ? 'auto' : 0 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.10, ease: 'easeIn' }}
          >
            {isExpanded && expandedRowRenderer(tableRow)}
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );

  function renderEditableCell(v: TableValue<IDType>, _i: number) {
    const helpers = getCellHelpers(v);

    const handleChange = (newValue: any) => {
      // Note: Always stage the typed/picked value as a draft. The cell shows it immediately;
      // the consumer's save handler (if any) decides when to persist it. The corresponding
      // clearDraft fires after the matching save completes (onChangeDebounced/onBlur
      // paths below; consumers in custom renders can call helpers.draft.clear()).
      setDraft(v.name, newValue);
      if (v.onChange) v.onChange(draftedRow, newValue);
      if (v.onChangeDebounced) scheduleDebounced(v, newValue);
    };

    const handleBlur = async (newValue: any, initialValue: any) => {
      if (newValue === initialValue) return;
      setIsWorking(true);
      await v.onBlur!(draftedRow, newValue);
      clearDraft(v.name);
      setIsWorking(false);
    };

    switch (v.type) {
      case 'string':
      case 'email':
        return (
          <div className="relative w-full">
            <TextField
              disabled={(v.disabled ?? false) || isWorking}
              value={getDisplayValue(v) ?? ''}
              size="small"
              type={v.type}
              fullWidth
              onFocus={(e) => {
                focusValueRef.current[getCellFocusKey(v.name)] = e.target.value;
              }}
              onChange={(e) => handleChange(e.target.value)}
              {...(v.onBlur && {
                onBlur: async (e) => {
                  const key = getCellFocusKey(v.name);
                  const initialValue = focusValueRef.current[key];
                  delete focusValueRef.current[key];
                  await handleBlur(e.target.value, initialValue);
                },
              })}
              inputRef={helpers.tabLock.registerInputRef}
              onKeyDown={helpers.tabLock.handleLockedTabbing}
            />
            {isWorking && (
              <span className="absolute right-2 top-1/2 -translate-y-1/2">
                <FontAwesomeIcon icon={faSpinner} spin />
              </span>
            )}
          </div>
        );

      case 'integer':
        return (
          <NumberField.Root
            disabled={v.disabled || isWorking}
            format={{ style: 'decimal', minimumFractionDigits: 0, maximumFractionDigits: 0 }}
            value={getDisplayValue(v)}
            className="h-full bg-theme-surface-2"
            onValueChange={(val) => {
              focusValueRef.current[`${getCellFocusKey(v.name)}::parsed`] = val;
              handleChange(val);
            }}
          >
            <NumberField.Group className="relative flex gap-0.5 h-full border border-theme-border rounded-sm">
              <NumberField.Decrement />
              <NumberField.Input
                className="h-full w-full px-1 py-2 bg-theme-surface-2 text-theme-text disabled:text-theme-text-muted transition"
                onFocus={() => {
                  focusValueRef.current[getCellFocusKey(v.name)] = v.value;
                  focusValueRef.current[`${getCellFocusKey(v.name)}::parsed`] = v.value;
                }}
                {...(v.onBlur && {
                  onBlur: async () => {
                    const key = getCellFocusKey(v.name);
                    const initialValue = focusValueRef.current[key];
                    const parsedValue = focusValueRef.current[`${key}::parsed`] ?? initialValue;
                    delete focusValueRef.current[key];
                    delete focusValueRef.current[`${key}::parsed`];
                    await handleBlur(parsedValue, initialValue);
                  },
                })}
              />
              {isWorking && (
                <span className="absolute top-1/2 right-1/2 -translate-y-1/2 translate-x-1/2">
                  <FontAwesomeIcon icon={faSpinner} spin />
                </span>
              )}
              <NumberField.Increment />
            </NumberField.Group>
          </NumberField.Root>
        );

      case 'decimal':
        return (
          <NumberField.Root
            disabled={v.disabled || isWorking}
            value={getDisplayValue(v)}
            format={{ style: 'decimal', minimumFractionDigits: 0, maximumFractionDigits: 2 }}
            className="h-full bg-theme-surface-2"
            onValueChange={(val) => {
              focusValueRef.current[`${getCellFocusKey(v.name)}::parsed`] = val;
              handleChange(val);
            }}
          >
            <NumberField.Group className="relative flex gap-0.5 h-full border border-theme-border rounded-sm">
              <NumberField.Input
                className="h-full w-full px-1 py-1 bg-theme-surface-2 text-theme-text disabled:text-theme-text-muted transition"
                onFocus={() => {
                  focusValueRef.current[getCellFocusKey(v.name)] = v.value;
                  focusValueRef.current[`${getCellFocusKey(v.name)}::parsed`] = v.value;
                }}
                {...(v.onBlur && {
                  onBlur: async () => {
                    const key = getCellFocusKey(v.name);
                    const initialValue = focusValueRef.current[key];
                    const parsedValue = focusValueRef.current[`${key}::parsed`] ?? initialValue;
                    delete focusValueRef.current[key];
                    delete focusValueRef.current[`${key}::parsed`];
                    await handleBlur(parsedValue, initialValue);
                  },
                })}
              />
              {isWorking && (
                <span className="absolute top-2 right-2">
                  <FontAwesomeIcon icon={faSpinner} spin />
                </span>
              )}
            </NumberField.Group>
          </NumberField.Root>
        );

      case 'boolean':
        return (
          <FormControlLabel
            control={
              <Checkbox
                disabled={v.disabled}
                checked={!!getDisplayValue(v)}
                onChange={e => handleChange(e.target.checked)}
                slotProps={{ input: { "aria-label": v.name } }}
                style={{ padding: '2px 9px 6px' }}
                className="!text-theme-text"
                size="small"
                ref={helpers.tabLock.registerInputRef}
                onKeyDown={helpers.tabLock.handleLockedTabbing}
              />
            }
            slotProps={{ typography: { className: '!text-theme-text' } }}
            label=""
            labelPlacement="top"
            sx={{ margin: 0 }}
          />
        );

      case 'date':
        return (
          <ButtonDatePicker
            disabled={v.disabled}
            className="w-full"
            value={getDisplayValue(v) ?? null}
            onChange={e => handleChange(e)}
            slotProps={{ field: { "aria-label": v.name } }}
            inputRef={helpers.tabLock.registerInputRef}
          />
        );

      case 'select':
        return (
          <Autocomplete
            classes={{ root: 'w-full' }}
            size="small"
            disabled={isWorking || v.disabled}
            value={v.options?.find((opt) => opt.value === getDisplayValue(v)) ?? null}
            options={v.options ?? []}
            onChange={async (_, val) => {
              const next = val?.value ?? null;
              setDraft(v.name, next);
              if (v.onChange) {
                setIsWorking(true);
                await v.onChange(tableRow, next);
                setIsWorking(false);
                clearDraft(v.name);
              }
              if (v.onChangeDebounced) scheduleDebounced(v, next);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={helpers.tabLock.registerInputRef}
                onKeyDown={helpers.tabLock.handleLockedTabbing}
                label={v.name}
                slotProps={{
                  ...params.slotProps,
                  input: {
                    ...params.slotProps.input,
                    endAdornment: (
                      <>
                        {isWorking && <FontAwesomeIcon icon={faSpinner} spin />}
                        {params.slotProps.input.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
            getOptionLabel={(option) => camelToCapitalized(option.label) ?? ''}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>{camelToCapitalized(option.label)}</li>
            )}
          />
        );

      default:
        return <span>{v.value}</span>;
    }
  }

  function renderReadOnlyCell(v: TableValue<IDType>) {
    switch (v.type) {
      case 'percentage': return <span>{v.value}%</span>;
      case 'decimal': return <span>{v.value.toFixed(2)}</span>;
      case 'boolean': return <span>{v.value
        ? <FontAwesomeIcon icon={faCheckSquare} className="text-theme-text-muted text-lg rounded px-0.5" />
        : <FontAwesomeIcon icon={faSquare} className="text-theme-text-muted text-lg rounded px-0.5" />}
      </span>;
      case 'date': return <span>{v.value}</span>;
      case 'select': return <span>{camelToCapitalized(v.options?.find((opt) => +opt.value === +v.value)?.label ?? v.value)}</span>;
      default: return <span>{v.value}</span>;
    }
  }
}

export const TableRowElement = memo(
  TableRowElementComponent
) as <IDType extends number | string = number>(
  props: TableRowElementProps<IDType>
) => JSX.Element;